import React, { useState, useEffect, useRef } from 'react';
import { fetchTestimonials } from '../lib/cms';

export default function ReviewsSlider({
  title = "Læs andres oplevelser.",
  subtitle = "Når tryghed, nærvær og faglighed mødes, skabes der rum for reel forandring og personlig vækst."
} = {}) {
  const [reviews, setReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    async function loadReviews() {
      const data = await fetchTestimonials();
      setReviews(data);
    }
    loadReviews();
  }, []);

  const toggleExpand = (id) => {
    setExpandedReviews((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Auto progression every 7 seconds when not paused
  useEffect(() => {
    if (reviews.length <= 1 || isPaused) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 7000);

    return () => clearInterval(timer);
  }, [reviews.length, isPaused]);

  if (!reviews || reviews.length === 0) return null;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 50) {
      handleNext();
    } else if (distance < -50) {
      handlePrev();
    }
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Helper to get visible 3 items cyclically for smooth desktop grid
  const getVisibleItems = () => {
    const total = reviews.length;
    const items = [];
    for (let i = 0; i < Math.min(3, total); i++) {
      items.push(reviews[(currentIndex + i) % total]);
    }
    return items;
  };

  const visibleReviews = getVisibleItems();

  return (
    <section
      className="py-20 lg:py-24 bg-white border-y border-[#EFEAE2] overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      aria-label="Klientudtalelser og anmeldelser"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#233424] tracking-tight">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-2 text-base text-[#566555] max-w-xl">
                {subtitle}
              </p>
            )}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center gap-3 self-start md:self-auto">
            <button
              type="button"
              onClick={handlePrev}
              className="h-10 w-10 text-[#233424] hover:text-black flex items-center justify-center transition-colors focus:outline-none cursor-pointer"
              aria-label="Forrige anmeldelse"
            >
              <i className="fa-solid fa-arrow-left text-base" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="h-10 w-10 text-[#233424] hover:text-black flex items-center justify-center transition-colors focus:outline-none cursor-pointer"
              aria-label="Næste anmeldelse"
            >
              <i className="fa-solid fa-arrow-right text-base" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Reviews Grid (Responsive: 1 col on mobile, 2 cols on tablet, 3 cols on desktop) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
          {visibleReviews.map((item, idx) => {
            const isExpanded = !!expandedReviews[item.id];
            return (
              <div
                key={`${item.id}-${idx}`}
                className={`bg-white rounded-2xl p-7 md:p-8 shadow-[0_16px_45px_rgba(44,62,45,0.08)] hover:shadow-[0_24px_60px_rgba(44,62,45,0.14)] flex flex-col justify-between transition-all duration-300 ${
                  idx === 1 ? 'hidden md:flex' : ''
                } ${idx === 2 ? 'hidden lg:flex' : ''}`}
              >
                <div>
                  {/* Subtle Quote Symbol */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex text-black gap-1">
                      <i className="fa-solid fa-star text-xs" aria-hidden="true" />
                      <i className="fa-solid fa-star text-xs" aria-hidden="true" />
                      <i className="fa-solid fa-star text-xs" aria-hidden="true" />
                      <i className="fa-solid fa-star text-xs" aria-hidden="true" />
                      <i className="fa-solid fa-star text-xs" aria-hidden="true" />
                    </div>
                    <i className="fa-solid fa-quote-right text-2xl text-[#E5DCD0]" aria-hidden="true" />
                  </div>

                  {/* Review Text with Read More */}
                  <div
                    className={`text-sm md:text-[15px] leading-relaxed text-[#3F4C3E] space-y-2 font-normal ${
                      !isExpanded ? 'line-clamp-3' : ''
                    }`}
                    dangerouslySetInnerHTML={{ __html: item.text }}
                  />

                  <button
                    type="button"
                    onClick={() => toggleExpand(item.id)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3A4F3D] hover:text-[#C67D5A] mt-3 cursor-pointer transition-colors"
                  >
                    <span>{isExpanded ? 'Læs mindre' : 'Læs mere'}</span>
                    <i
                      className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} text-[10px]`}
                      aria-hidden="true"
                    />
                  </button>
                </div>

                {/* Author Info */}
                <div className="pt-5 mt-5 border-t border-[#E3D9CC]">
                  <h4 className="text-base font-semibold text-[#233424] font-serif">
                    {item.name}
                  </h4>
                  {item.age && (
                    <p className="text-xs text-[#70806E] mt-0.5">
                      {item.age}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Indicators */}
        <div className="flex justify-center items-center gap-2 mt-8">
          {reviews.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCurrentIndex(i)}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                i === currentIndex ? 'w-8 bg-black' : 'w-2 bg-[#D1D5DB] hover:bg-black'
              }`}
              aria-label={`Gå til udtalelse ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
